import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { SiteController } from "../../lib/site-controllers";
import { areasApi } from "../../lib/areas";
import { groupsApi } from "../../lib/groups";
import { presetsApi } from "../../lib/presets";
import { useAuth } from "@/hooks/useAuth";
import { AlertTriangle, CheckCircle2, XCircle, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AreaControl from "../areas/AreaControl";

interface ControllerControlProps {
  controller: SiteController;
  siteId: string;
  onSetCurrentPreset: (areaId: string, presetId: string | null, presetAddress?: number | null) => void;
  canConfigure?: boolean;
}

export default function SiteControllerControl({
  controller,
  siteId,
  onSetCurrentPreset,
  canConfigure = false,
}: ControllerControlProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: areas, isLoading: areasLoading } = useQuery({
    queryKey: ["areas", controller.id],
    queryFn: () => areasApi.getBySiteController(controller.id),
    enabled: !!controller.id,
  });

  const userArea = useMemo(
    () => areas?.find((a) => a.type === "user") ?? null,
    [areas]
  );

  const { data: userAreaPresets } = useQuery({
    queryKey: ["presets", "user-area", userArea?.id],
    queryFn: () => presetsApi.getByArea(userArea!.id),
    enabled: !!userArea?.id,
    staleTime: 30_000,
  });

  const activeUserLabel = useMemo(() => {
    const presetId = userArea?.currentPresetId;
    if (!presetId) return null;
    const preset = userAreaPresets?.find((p) => p.id === presetId);
    if (!preset) return null;
    return (
      preset.userGroupName ??
      preset.dashboardName ??
      preset.name ??
      null
    );
  }, [userArea?.currentPresetId, userAreaPresets]);

  const lightAreas = useMemo(
    () => areas?.filter((a) => a.type === "light") ?? [],
    [areas]
  );

  const isEndUser = user?.role === "end_user";
  const { data: siteGroups, isLoading: groupsLoading } = useQuery({
    queryKey: ["groups", siteId],
    queryFn: () => groupsApi.getBySite(siteId),
    enabled: !!siteId && isEndUser,
  });

  const userGroupIds = useMemo(
    () => siteGroups?.map((g) => g.id) ?? [],
    [siteGroups]
  );

  const presetQueries = useQueries({
    queries: lightAreas.map((area) => ({
      queryKey: ["presets", area.id] as const,
      queryFn: () => presetsApi.getByArea(area.id),
      enabled: isEndUser && lightAreas.length > 0 && userGroupIds.length > 0,
    })),
  });

  const visibleLightAreas = useMemo(() => {
    if (!lightAreas.length) return [];
    if (!isEndUser) return lightAreas;
    return lightAreas.filter((_, i) => {
      const presets = presetQueries[i]?.data;
      if (!presets) return false;
      return presets.some((p) =>
        p.groups.some((g) => userGroupIds.includes(g.groupId))
      );
    });
  }, [lightAreas, isEndUser, presetQueries, userGroupIds]);

  const handleConfigure = () => {
    // navigate(`/sites/${siteId}/controllers/${controller.id}/configure`);
    navigate(`/sites/${siteId}`);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <div id="controller-info" className="flex-col items-center gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {controller.state === "online" ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                    ) : controller.state === "warning" ? (
                      <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-destructive shrink-0" />
                    )}
                    <h3 className="font-medium">{controller.name}</h3>
                  </div>
                  {controller.state === "warning" && controller.stateDescription?.trim() && (
                    <p className="text-xs text-amber-900 dark:text-amber-200 pl-7 max-w-xl">
                      {controller.stateDescription}
                    </p>
                  )}
                </div>
                {canConfigure && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {controller.ipAddress && <p>IP: {controller.ipAddress}</p>}
                    {controller.lastSeen && (
                      <p className="text-xs text-muted-foreground">
                        Last seen:{" "}
                        {new Date(controller.lastSeen).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div
                id="controller-status"
                className="flex flex-col items-end gap-2"
              >
                {activeUserLabel && (
                  <Badge variant="default" className="text-sm font-semibold">
                    User Group: {activeUserLabel}
                  </Badge>
                )}
                <div>
                  {canConfigure && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleConfigure}
                      className="text-xs sm:text-sm"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Configure
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas Section */}
        <div className="mt-4 pt-4 border-t">
          {/* <h4 className="text-lg font-semibold mb-3">Presets</h4> */}
          {areasLoading ? (
            <p className="text-sm text-muted-foreground">Loading areas...</p>
          ) : isEndUser && (groupsLoading || presetQueries.some((q) => q.isLoading)) ? (
            <p className="text-sm text-muted-foreground">Loading presets...</p>
          ) : areas && areas.length > 0 ? (
            <div className="space-y-4">
              {visibleLightAreas.map((area) => (
                <AreaControl
                  key={area.id}
                  area={area}
                  siteId={siteId}
                  onSetCurrentPreset={onSetCurrentPreset}
                  controllerOnline={controller.state !== "offline"}
                  disabled={controller.state === "offline"}
                />
              ))}
            </div>
          ) : null}
          {areas && areas.length > 0 && visibleLightAreas.length === 0 && !groupsLoading && !presetQueries.some((q) => q.isLoading) ? (
            <p className="text-sm text-muted-foreground">
              {isEndUser
                ? "No areas with preset access for your group"
                : "No light areas on this controller"}
            </p>
          ) : areas?.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No areas configured for this controller
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
